import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form, FormGroup, FormLabel } from 'react-bootstrap';
import axiosInstance from '../../../../services/AxiosInstance';
import Api from '../../../../constants/Api';
import { errorAlert, successAlert } from '../../alert/defaultAlert';
import { formatDateTime } from '../../../../utils/DateUtils';
import { convertDoubleToCurrencyPTBR, convertStringToStringDouble } from '../../../../utils/ConverterUtils';
import { formatNumberWithTwoDecimalPlaces } from '../../../../utils/FormatUtils';
import ModalPageEvent from '../../Common/Modal/ModalPageEvent';
import SegmentedButtons from '../../Forms/Element/SegmentedButtons';
import InputDecimal from '../../Forms/Element/InputDecimal';
import TextArea from '../../Forms/Element/TextArea';
import PaymentMethodSelect from '../Common/PaymentMethod/PaymentMethodSelect';
import PaymentMethod from '../Common/PaymentMethod/PaymentMethod';
import './AppointmentDeposit.css';

/**
 * Situações da reserva e como cada uma aparece. O PENDING nasce no servidor quando o serviço
 * exige reserva e ela ainda não foi paga; RETAINED é a reserva paga de um agendamento que o
 * cliente cancelou ou faltou. Textos em pt-BR porque este bloco da agenda já é todo assim.
 */
export const DEPOSIT_SITUATIONS = {
    PENDING: { icon: 'fa-solid fa-shield-halved', css: 'is-pending', label: 'Reserva pendente', hint: 'O serviço exige reserva para garantir o horário. Receba antes de dar o horário como garantido.' },
    PAID: { icon: 'fa-solid fa-shield-halved', css: 'is-paid', label: 'Reserva paga', hint: 'Será abatida do total ao gerar a venda.' },
    RETAINED: { icon: 'fa-solid fa-lock', css: 'is-retained', label: 'Reserva retida', hint: 'O cliente cancelou ou faltou, então o valor fica com a casa.' },
    APPLIED: { icon: 'fa-solid fa-circle-check', css: 'is-applied', label: 'Reserva abatida na venda', hint: 'Já descontada do total da venda.' },
    REFUNDED: { icon: 'fa-solid fa-rotate-left', css: 'is-refunded', label: 'Reserva devolvida', hint: 'O valor foi devolvido ao cliente.' }
};

const OPEN_STATUSES = ['SCHEDULED', 'CONFIRMED', 'IN_PROGRESS'];

/** Ícone compacto da agenda. Não renderiza nada quando o agendamento não tem reserva nem exigência. */
export const AppointmentDepositBadge = ({ situation, value }) => {
    const meta = DEPOSIT_SITUATIONS[situation];
    if (!meta) {
        return null;
    }
    const amount = value != null ? ` (${convertDoubleToCurrencyPTBR(value)})` : '';
    return (
        <i className={`${meta.icon} appointment-deposit-badge ${meta.css}`}
            title={`${meta.label}${amount}`}
            aria-label={meta.label} />
    );
};

/**
 * Rascunho da reserva a receber ao criar o agendamento. A sugestão de tipo e valor vem do primeiro
 * serviço que exige reserva; a recepção pode ajustar. receiveNow desligado significa "receber
 * depois": o agendamento nasce com a reserva pendente e é cobrada pelo detalhe.
 */
export const buildDepositDraft = (serviceOptions = []) => {
    const requiring = serviceOptions.map(option => option?.service).find(service => service?.requiresDeposit);
    return {
        receiveNow: false,
        type: requiring?.depositType || 'AMOUNT',
        input: formatNumberWithTwoDecimalPlaces(requiring?.depositValue ?? 0),
        paymentMethod: null
    };
};

/** Algum dos serviços escolhidos exige reserva? Decide se a seção aparece no formulário. */
export const requiresDeposit = (serviceOptions = []) =>
    serviceOptions.some(option => option?.service?.requiresDeposit === true);

/** Valor em reais da reserva: o digitado, ou o percentual do preço total do agendamento. */
export const depositPreviewValue = (draft, totalPrice) => {
    const input = Number(convertStringToStringDouble(draft?.input) || 0);
    return draft?.type === 'PERCENT' ? (Number(totalPrice || 0) * input) / 100 : input;
};

/** Mensagem de erro do rascunho, ou null quando pode seguir. Só vale com receiveNow ligado. */
export const validateDepositDraft = (draft, totalPrice) => {
    if (!draft?.receiveNow) {
        return null;
    }
    if (!draft.paymentMethod?.value) {
        return 'Escolha a forma de recebimento da reserva.';
    }
    if (!(depositPreviewValue(draft, totalPrice) > 0)) {
        return 'Informe o valor da reserva.';
    }
    return null;
};

/** Registra a reserva de um agendamento já criado. Devolve a promessa da chamada. */
export const registerDeposit = (appointmentId, draft) => axiosInstance.post(
    `${Api.appointment.deposit}/appointment/${appointmentId}`, {
        type: draft.type,
        input: Number(convertStringToStringDouble(draft.input) || 0),
        paymentMethod: draft.paymentMethod.value
    });

/**
 * Campos da reserva (tipo, valor, forma de recebimento e prévia). Usado no modal de recebimento
 * do detalhe e no formulário de criação do agendamento, para os dois não divergirem.
 */
export const AppointmentDepositFields = ({ draft, onChange, totalPrice, disabled = false }) => {
    const update = (patch) => onChange({ ...draft, ...patch });
    const previewValue = depositPreviewValue(draft, totalPrice);

    return (
        <div>
            <FormGroup className="mb-3">
                <FormLabel>Como calcular</FormLabel>
                <SegmentedButtons
                    compact
                    ariaLabel="Como calcular a reserva"
                    value={draft.type}
                    onChange={(value) => !disabled && update({ type: value })}
                    options={[
                        { value: 'AMOUNT', label: 'Valor (R$)' },
                        { value: 'PERCENT', label: 'Percentual (%)' }
                    ]} />
            </FormGroup>
            <FormGroup className="mb-3">
                <FormLabel>{draft.type === 'PERCENT' ? 'Percentual' : 'Valor'}</FormLabel>
                <InputDecimal
                    name="depositInput"
                    value={draft.input}
                    disabled={disabled}
                    onChange={(e) => update({ input: e.target.value })} />
            </FormGroup>
            <FormGroup>
                <FormLabel>Forma de recebimento</FormLabel>
                <PaymentMethodSelect
                    value={draft.paymentMethod}
                    onChange={(value) => update({ paymentMethod: value })}
                    hideSubscription
                    onlyMethods={[PaymentMethod.CASH, PaymentMethod.PIX, PaymentMethod.CREDIT_CARD, PaymentMethod.DEBIT_CARD]} />
            </FormGroup>
            <div className="appointment-deposit-form__preview">
                <span>Valor da reserva</span>
                <strong>{convertDoubleToCurrencyPTBR(previewValue)}</strong>
            </div>
        </div>
    );
};

/**
 * Seção "Reserva" dos formulários de criação: aparece quando algum serviço escolhido a exige e
 * deixa a recepção receber agora ou deixar pendente para cobrar depois.
 */
export const AppointmentDepositDraftSection = ({ draft, onChange, serviceOptions, totalPrice, disabled = false }) => {
    if (!requiresDeposit(serviceOptions) || !draft) {
        return null;
    }
    return (
        <div className="appointment-deposit-draft">
            <label className="appointment-deposit-draft__toggle" htmlFor="depositReceiveNow">
                <span>
                    <strong><i className="fa-solid fa-shield-halved me-2"></i>Reserva do horário</strong>
                    <small>Este serviço exige reserva. Receba agora para garantir o horário, ou deixe pendente.</small>
                </span>
                <Form.Check
                    type="switch"
                    id="depositReceiveNow"
                    label="Receber agora"
                    disabled={disabled}
                    checked={draft.receiveNow}
                    onChange={(e) => onChange({ ...draft, receiveNow: e.target.checked })} />
            </label>
            {draft.receiveNow && (
                <AppointmentDepositFields draft={draft} onChange={onChange} totalPrice={totalPrice} disabled={disabled} />
            )}
        </div>
    );
};

/**
 * Painel de reserva do detalhe do agendamento: mostra a situação e oferece receber ou devolver.
 *
 * @param appointment agendamento aberto no detalhe
 * @param serviceItems serviços do agendamento (trazem a configuração de reserva de cada um)
 * @param price preço total do agendamento, base do cálculo percentual
 * @param onChanged chamado depois de receber ou devolver, para a agenda atualizar o ícone
 */
const AppointmentDepositPanel = ({ appointment, serviceItems = [], price, onChanged }) => {
    const [deposit, setDeposit] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [showRegister, setShowRegister] = useState(false);
    const [showRefund, setShowRefund] = useState(false);
    const [draft, setDraft] = useState(buildDepositDraft());
    const [refundReason, setRefundReason] = useState('');
    const [saving, setSaving] = useState(false);

    // Serviço que exige reserva: a configuração dele sugere tipo e valor ao receber.
    const requiringService = useMemo(() => {
        const fromItems = serviceItems.map(item => item.service).find(service => service?.requiresDeposit);
        if (fromItems) {
            return fromItems;
        }
        return appointment?.service?.requiresDeposit ? appointment.service : null;
    }, [serviceItems, appointment?.service]);

    const load = useCallback(() => {
        if (!appointment?.id) {
            return;
        }
        axiosInstance.get(`${Api.appointment.deposit}/appointment/${appointment.id}`)
            .then((resp) => setDeposit(resp.status === 204 ? null : resp.data))
            .catch(() => setDeposit(null))
            .finally(() => setLoaded(true));
    }, [appointment?.id]);

    useEffect(() => {
        setLoaded(false);
        setDeposit(null);
        load();
    }, [load, appointment?.appointmentStatus]);

    const isOpen = OPEN_STATUSES.includes(appointment?.appointmentStatus);
    const hasActiveDeposit = deposit && deposit.status !== 'REFUNDED';
    const situationKey = deposit ? deposit.situation : (requiringService && isOpen ? 'PENDING' : null);
    const meta = DEPOSIT_SITUATIONS[situationKey];

    const openRegister = () => {
        // Sugestão do cadastro do serviço; a recepção pode ajustar para este agendamento.
        setDraft({ ...buildDepositDraft([{ service: requiringService }]), receiveNow: true });
        setShowRegister(true);
    };

    const handleRegister = () => {
        const problem = validateDepositDraft(draft, price);
        if (problem) {
            errorAlert(problem);
            return;
        }
        setSaving(true);
        registerDeposit(appointment.id, draft)
            .then((resp) => {
                successAlert('Reserva recebida com sucesso.');
                setDeposit(resp.data);
                setShowRegister(false);
                onChanged?.();
            })
            .catch((error) => errorAlert(error.response?.data?.message || error.message))
            .finally(() => setSaving(false));
    };

    const handleRefund = () => {
        setSaving(true);
        axiosInstance.patch(`${Api.appointment.deposit}/appointment/${appointment.id}/refund`, { reason: refundReason })
            .then((resp) => {
                successAlert('Reserva devolvida com sucesso.');
                setDeposit(resp.data);
                setShowRefund(false);
                setRefundReason('');
                onChanged?.();
            })
            .catch((error) => errorAlert(error.response?.data?.message || error.message))
            .finally(() => setSaving(false));
    };

    // Sem exigência e sem reserva gravada não há o que mostrar: o painel some, e o detalhe
    // continua igual para quem não usa o recurso.
    if (!loaded || !meta) {
        return null;
    }

    const canReceive = isOpen && !hasActiveDeposit;
    const canRefund = deposit?.status === 'PAID';

    return (
        <>
            <div className="appointment-notes-box appointment-deposit-panel">
                <div className="appointment-deposit-panel__head">
                    <div className="appointment-deposit-panel__status">
                        <i className={`${meta.icon} appointment-deposit-badge ${meta.css}`} />
                        <span>{meta.label}</span>
                        {deposit && <strong>{convertDoubleToCurrencyPTBR(deposit.value)}</strong>}
                    </div>
                    <div className="appointment-deposit-panel__actions">
                        {canReceive && (
                            <Button size="sm" variant="primary" onClick={openRegister}>
                                <i className="fa-solid fa-hand-holding-dollar me-2"></i>Receber reserva
                            </Button>
                        )}
                        {canRefund && (
                            <Button size="sm" variant="outline-danger" onClick={() => setShowRefund(true)}>
                                <i className="fa-solid fa-rotate-left me-2"></i>Devolver
                            </Button>
                        )}
                    </div>
                </div>
                <p className="appointment-deposit-panel__meta">
                    {meta.hint}
                    {deposit?.paidAt && ` Recebida em ${formatDateTime(deposit.paidAt)}${deposit.paidBy ? ` por ${deposit.paidBy}` : ''}.`}
                    {deposit?.status === 'REFUNDED' && deposit.refundReason && ` Motivo: ${deposit.refundReason}.`}
                </p>
            </div>

            <ModalPageEvent
                title="Receber reserva"
                show={showRegister}
                setShow={setShowRegister}
                btnText="Receber"
                btnOnClick={handleRegister}
                btnDisabled={saving}
                centered
                dialogClassName="appointment-cancel-modal"
                child={<AppointmentDepositFields draft={draft} onChange={setDraft} totalPrice={price} />}
            />

            <ModalPageEvent
                title="Devolver reserva"
                show={showRefund}
                setShow={setShowRefund}
                btnText="Devolver"
                btnOnClick={handleRefund}
                btnDisabled={saving || !refundReason.trim()}
                centered
                dialogClassName="appointment-cancel-modal"
                child={
                    <div>
                        <p className="mb-2">Use quando a falha foi da casa (colaborador faltou, agenda fechada). Sem devolução, a reserva fica retida.</p>
                        <TextArea
                            value={refundReason}
                            onChange={(e) => setRefundReason(e.target.value)}
                            placeholder="Motivo da devolução"
                            maxLength={255}
                            counter="true"
                            style={{ minHeight: '120px' }}
                        />
                    </div>
                }
            />
        </>
    );
};

export default AppointmentDepositPanel;
